// PartnerPage.jsx

import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  ButtonBase,
  useMediaQuery,
  useTheme,
  Grid,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
} from '@mui/material';
import Navbar from 'scenes/widgets/navbar';
import Footer from 'scenes/widgets/footer';
import { Link as RouterLink } from 'react-router-dom';
import WerbepartnerPage from 'scenes/werbepartner';
import ReactMarkdown from 'react-markdown';
import Countdown from 'react-countdown';
import PopupWidget from 'scenes/widgets/PopupWidget.jsx';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Grid as SwiperGrid } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/grid';
import { Textfit } from 'react-textfit'
import { useNavigate } from 'react-router-dom';
import DIVIDER_SVG from 'scenes/widgets/Divider_SVG.jsx';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const PartnerPage = () => {

  const isNonMobileScreens = useMediaQuery('(min-width:1000px)');

  const theme = useTheme();
  const [activeContent, setActiveContent] = useState(null);
  const [partnerArrowColorIndex, setPartnerArrowColorIndex] = useState(0);
  const [swiperInstance, setSwiperInstance] = useState(null); // <-- Add this state

  const navigate = useNavigate();

  const handleMehrErfahren = (type) => {
    setActiveContent((prevType) => (prevType === type ? null : type));
  };

  const [forceRerender, setForceRerender] = useState(0);

  // Auto-scrolling banner refs/state
  const scrollRef = useRef(null);
  const isHoveringBannerRef = useRef(false);
  const BASE_SCROLL_SPEED_PX_PER_SEC = 200;
  const HOVER_SCROLL_SPEED_PX_PER_SEC = 50;

  useEffect(() => {
    // Force a rerender after component mounts to trigger Textfit recalculation
    const timer = setTimeout(() => {
      setForceRerender(prev => prev + 1);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Infinite auto-scroll effect (seamless loop)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    let rafId;
    let lastTs = performance.now();
    let currentPos = el.scrollLeft;

    const tick = (ts) => {
      const deltaSeconds = (ts - lastTs) / 1000;
      lastTs = ts;

      // We render the logos twice; loop after one full set width.
      const loopWidth = el.scrollWidth / 2;
      if (loopWidth > 0) {
        const currentSpeed = isHoveringBannerRef.current
          ? HOVER_SCROLL_SPEED_PX_PER_SEC
          : BASE_SCROLL_SPEED_PX_PER_SEC;

        currentPos += currentSpeed * deltaSeconds;
        if (currentPos >= loopWidth) {
          currentPos -= loopWidth;
        }
        el.scrollLeft = currentPos;
      }

      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);

  // Replace the manual scroll handler with this (simple no-op now)
  const handleManualScroll = () => { };

  // LeftArrow Component
  const LeftArrow = ({ fills }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 527 864"
      style={{ width: '100%', height: '100%' }}
    >
      <defs>
        <style>
          {`
          .cls-1 { fill: ${fills[0]}; }
          .cls-2 { fill: ${fills[1]}; }
          .cls-3 { fill: ${fills[2]}; }
        `}
        </style>
      </defs>
      <path
        className="cls-3"
        d="M376.17,402.24c-7.39-.18-83.43-184.7-79.49-197.33,3.93-12.65,219.84-207.26,228.22-204.89,8.38,2.37-141.33,402.4-148.72,402.22Z"
      />
      <path
        className="cls-1"
        d="M376.67,491.63c6.31-1.52,157.61,367.81,149.76,371.84-7.84,4.03-220.02-162.68-223.16-173.33-3.14-10.65,67.11-196.99,73.4-198.5Z"
      />
      <path
        className="cls-2"
        d="M352.95,448.18c-.04,11.7-77.31,220.16-82.85,223.77-5.53,3.59-270.11-189.13-270.11-209.81,0-21.27,257.5-238.68,263.35-236.45,5.86,2.23,89.64,210.8,89.6,222.5Z"
      />
    </svg>
  );

  // RightArrow Component
  const RightArrow = ({ fills }) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 527 864"
      style={{ width: '100%', height: '100%' }}
    >
      <defs>
        <style>
          {`
          .cls-1 { fill: ${fills[0]}; }
          .cls-2 { fill: ${fills[1]}; }
          .cls-3 { fill: ${fills[2]}; }
        `}
        </style>
      </defs>
      <path
        className="cls-3"
        d="M150.56,402.24c7.39-.18,83.43-184.7,79.49-197.33C226.12,192.25,10.22-2.35,1.84.02c-8.38,2.37,143.17,402.4,150.72,402.22Z"
      />
      <path
        className="cls-1"
        d="M150.05,491.63c-6.31-1.52-157.61,367.81-149.76,371.84,7.84,4.03,220.02-162.68,223.16-173.33,3.14-10.65-67.11-196.99-73.4-198.5Z"
      />
      <path
        className="cls-2"
        d="M173.77,448.18c.04,11.7,77.31,220.16,82.85,223.77,5.53,3.59,270.11-189.13,270.11-209.81,0-21.27-257.5-238.68-263.35-236.45-5.86,2.23-89.64,210.8-89.6,222.5Z"
      />
    </svg>
  );

  const arrowColorSets = [
    ['#1a5e7d', '#2074b8', '#60ade1'],
    ['#854d74', '#c796b7', '#8e1861'],
    ['#b39618', '#f2e198', '#e2cb4d']
  ];

  // Custom marker icons with circular background
  const createCircularIcon = (iconUrl, size = 40, isKK = false) => {
    return L.divIcon({
      html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        border-radius: 50%;
        background-color: white;
        border: 2px solid black;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        z-index: ${isKK ? '1000' : '100'};
        position: relative;
      ">
        <img src="${iconUrl}" style="
          width: ${size - 8}px;
          height: ${size - 8}px;
          object-fit: contain;
          border-radius: 50%;
        " />
      </div>
    `,
      className: 'custom-marker-icon',
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
      popupAnchor: [0, -size / 2],
    });
  };

  const partnerIcon = createCircularIcon('/assets/wettbewerb/posts/KK_Rohling_Muster1.jpg', 40, false);
  const kunstkaleidoskopIcon = createCircularIcon('/KK_Logo_512.svg', 50, true);

  const kunstkaleidoskopLocation = {
    name: 'Kunstkaleidoskop',
    website: 'https://kunstkaleidoskop.de/',
    map: 'https://maps.app.goo.gl/d2sBfyJ7XvTwQrDBA',
    lat: 53.545466346578486,
    lon: 9.988597540259981,
  };

  const partnerLogos = [
      {
      logo: '/assets/partner/logos/InvestBulgaria.png',
      name: 'InvestBulgaria',
      description: '',
      website: 'https://www.investbulgaria.org/',
    },
    {
      logo: '/assets/partner/logos/CiaoMamma.png',
      name: 'Ciao Mamma!',
      description: '',
      website: 'http://www.ciaomamma.de/',
      map: 'https://maps.app.goo.gl/8hB7x1xW6YhnLPPW6',
      lat: 53.54313437841149,
      lon: 9.985373246353756,
    },
    {
      logo: '/assets/partner/logos/Tom-Sumo.png',
      name: 'Tomsumo Hamburg GmbH',
      description: '',
      website: 'https://tom-sumo-hamburg.eatbu.com/',
      map: 'https://maps.app.goo.gl/G7P4NPBhKoCZJ8fB6',
      lat: 53.55106746121994,
      lon: 9.994019809487451,
    },
    {
      logo: '/assets/partner/logos/sencha.png',
      name: 'Sencha Sushi Bar & Restaurant',
      description: '',
      website: 'https://www.instagram.com/sencha_hafencity/',
      map: 'https://maps.app.goo.gl/aRbpYT5xB2bpNcWo6',
      lat: 53.54383985012659,
      lon: 9.997725070861332,
    },
    {
      logo: '/assets/partner/logos/ernst-brendler.jpg',
      name: 'Ernst Brendler',
      description: 'Ernst Brendler ist seit 1879 Spezialist für Tropenbekleidung aus reinen Naturfasern.\nVom Standort im Herzen Hamburgs werden Reedereien, Marine und Abenteurer gleichermaßen beliefert.\nDas Sortiment umfasst Herren-, Damen - und Kinderbekleidung in hoher Qualität und traditioneller 5. Generation.',
      website: 'https://www.ernst-brendler.de/',
      map: 'https://maps.app.goo.gl/UTWg9qxCBKhppqcBA',
      lat: 53.54933041833549,
      lon: 9.99233102491586,
    },
    {
      logo: '/assets/partner/logos/budni_logo.svg',
      name: 'budni',
      description: '',
      website: 'https://www.budni.de/',
      map: 'https://maps.app.goo.gl/bxxmUvuWRnhfjbVp7',
      lat: 53.540890062374785,
      lon: 9.998313496078945,
    },
    {
      logo: '/assets/partner/logos/nohrd-3-2.png',
      name: 'NOHRD',
      description: '',
      website: 'https://www.nohrd.com/',
      map: 'https://maps.app.goo.gl/xw54SnVUcaQDbjs26',
      lat: 53.554516159510925,
      lon: 10.002644007989936,
    },
    {
      logo: '/assets/partner/logos/petrateufel.webp',
      name: 'Petra Teufel',
      description: '',
      website: 'https://petrateufel.de/',
      map: 'https://maps.app.goo.gl/xRtLErNN8Taw9H2n7',
      lat: 53.587087552862364, 
      lon: 9.984840484442376,
    },
    {
      logo: '/assets/partner/logos/airpower Logo_mittel_neu-2e4d6fcd.webp',
      name: 'airpower europe GmbH',
      description: '',
      website: 'https://www.airpower-gmbh.com/',
      map: 'https://maps.app.goo.gl/7vGLovzwbrMniEAR8',
      lat: 50.5565183, 
      lon: 7.2987866730164646,
    },
    {
      logo: '/assets/partner/logos/r2handwerk-p-500.png',
      name: 'r2 handwerk',
      description: '',
      website: 'https://www.r2handwerk.de/',
      map: 'https://maps.app.goo.gl/GDCvjH3DXWaXrp4A7',
      lat: 53.55487057235058, 
      lon: 10.000954040260414,
    },
    {
      logo: '/assets/partner/logos/kichi.jpg',
      name: 'Kichi Vietnamese Foodtour',
      description: '',
      website: 'https://www.kichi-hamburg.com/',
      map: 'https://maps.app.goo.gl/96VFdJ9eREjKcQre7',
      lat: 53.55291256025953, 
      lon: 10.02036961142424,
    },
    {
      logo: '/assets/partner/logos/Vattenfall.svg',
      name: 'Vattenfall',
      description: '',
      website: 'https://www.vattenfall.de/',
      map: 'https://maps.app.goo.gl/kZBqU2UDtyGMA6XbA',
      lat: 53.54394615863356, 
      lon: 9.9943318963332,
    },
    {
      logo: '/assets/partner/logos/hanse-aesthetic-signet-ha-solid-gold-spot-800-300x300.png',
      name: 'HANSE AESTHETIC Hamburg',
      description: '',
      website: 'https://www.hanse-aesthetic.de/',
      map: 'https://maps.app.goo.gl/naVwP8GmqRoMHJDV9',
      lat: 53.5511121106388, 
      lon: 9.988124386293178,
    },
    {
      logo: '/assets/partner/logos/worksmart.png',
      name: 'Worksmart',
      description: '',
      website: 'https://www.worksmart.de/',
      map: 'https://maps.app.goo.gl/G4CdW85o5TKaE6xeA',
      lat: 53.54875173754885, 
      lon: 9.988496226768387,
    },
    {
      logo: '/assets/partner/logos/MH-125-Logo-final_ohne-Abstand_grau-1536x704.png',
      name: 'Miehlmann.Hertz',
      description: '',
      website: 'https://www.miehlmannhertz.de/',
      map: 'https://maps.app.goo.gl/Tn8Y3k2FXwdXi7XWA',
      lat: 53.550627386248685, 
      lon: 9.998034074869006,
    },
    {
      logo: '/assets/partner/logos/heuking.png',
      name: 'HEUKING',
      description: '',
      website: 'https://www.heuking.de/',
      map: 'https://maps.app.goo.gl/xmR3hjVqvBUypT3v5',
      lat: 53.5507334255715, 
      lon: 9.988288984440599,
    },
    {
      logo: '/assets/partner/logos/hanse-psychotherapie-hamburg-logo-name-250x111.png',
      name: 'HANSE Psychotherapie',
      description: '',
      website: 'https://hanse-psychotherapie.de/',
      map: 'https://maps.app.goo.gl/r7AMKhjRi8uEWHmU8',
      lat: 53.55196641360548, 
      lon: 9.989600238650661,
    },
    {
      logo: '/assets/partner/logos/TRADER.jpg',
      name: 'TRADER',
      description: '',
      website: 'https://www.instagram.com/trader.gallery/',
      map: 'https://maps.app.goo.gl/XZSMJtxXbSXkpvHX7',
      lat: 53.56229517163965, 
      lon: 9.964406226769011,
    },
    {
      logo: '/assets/partner/logos/nelandluna-logo.jpg',
      name: 'Nel & Luna',
      description: '',
      website: 'https://www.nelandluna.com/',
      map: 'https://maps.app.goo.gl/C6vQyfJ7QYyhrb9GA',
      lat: 53.58357467918863, 
      lon: 10.010054828622614,
    },
    {
      logo: '/assets/partner/logos/cloe_and_leo.jpg',
      name: 'CLOE & LEO',
      description: '',
      website: 'https://cloeandleo.de/',
      map: 'https://maps.app.goo.gl/VcC6tNKdJKRiQbdd8',
      lat: 53.58510216179748, 
      lon: 9.980638638409399,
    },
    // {
    //   logo: '/assets/partner/logos/TheNikolaiHotelHamburg.jpg',
    //   name: 'The Nikolai Hotel Hamburg',
    //   description: '',
    //   website: 'https://www.leonardo-hotels.com/hamburg/the-nikolai-hotel-hamburg-leonardo-limited-edition?utm_medium=link_site_gmb&utm_campaign=the-nikolai-hotel-hamburg-leonardo-limited-edition',
    // },
    {
      logo: '/assets/partner/logos/dermatologie-neuer-wall.webp',
      name: 'MVZ Dermatologie am Neuen Wall',
      description: '',
      website: 'https://derma-neuerwall.de/',
      map: 'https://maps.app.goo.gl/Q42ovYKhpedtrsun8',
      lat: 53.55235805442749, 
      lon: 9.991374170948916,
    },
    {
      logo: '/assets/partner/logos/Bluebird.webp',
      name: 'Bluebird Legal & Tax',
      description: '',
      website: 'https://www.bluebird.tax/',
      map: 'https://maps.app.goo.gl/gdxTCvVkXnULX3vG9',
      lat: 53.552852085250905, 
      lon: 9.987469888345261,
    },
    {
      logo: '/assets/partner/logos/myclassicocom_logo.png',
      name: 'myCLASSICO.com',
      description: '',
      website: 'https://www.myclassico.com/',
      map: 'https://maps.app.goo.gl/Gc69KYtzcLwWibti7',
      lat: 53.55287416273615, 
      lon: 9.98908438629326,
    },
    {
      logo: '/assets/partner/logos/hinode_logo.png',
      name: 'HINODE Japanese Restaurant',
      description: '',
      website: 'https://hinode-hamburg.de/',
      map: 'https://maps.app.goo.gl/q5UnW2j7SKaoTkHq9',
      lat: 53.584458355634915, 
      lon: 9.97862915560618,
    },
    {
      logo: '/assets/partner/logos/VON-POLL-IMMOBILIEN-LOGO-CMYK.jpg',
      name: 'VON POLL IMMOBILIEN',
      description: '',
      website: 'https://www.von-poll.com/',
      map: 'https://maps.app.goo.gl/jUBY1ih6ymN7aiH49',
      lat: 53.56854567301644, 
      lon: 10.019731597636117,
    },
    {
      logo: '/assets/partner/logos/luxussachen.svg',
      name: 'Luxussachen.com',
      description: '',
      website: 'https://www.luxussachen.com/',
      map: 'https://maps.app.goo.gl/mGkUzjdBrxkBsLah7',
      lat: 53.55339323176171, 
      lon: 9.989516296079975,
    },
    {
      logo: '/assets/partner/logos/LE_BIG_TAMTAM.svg',
      name: 'Le big TamTam',
      description: '',
      website: 'https://lebigtamtam.com/',
      map: 'https://maps.app.goo.gl/582uJjJD7A9sDQeVA',
      lat: 53.55383040354991, 
      lon: 9.992249101637602,
    },
    {
      logo: '/assets/partner/logos/Schanzenoptik.png',
      name: 'Schanzenoptik',
      description: '',
      website: 'https://www.schanzenoptik.de/',
      map: 'https://maps.app.goo.gl/ZGVuPfVs5jzJSjhT8',
      lat: 53.56056209876379, 
      lon: 9.965219796080333,
    },
    {
      logo: '/assets/partner/logos/Optiko_240x70.webp',
      name: 'Optiko Hamburg',
      description: '',
      website: 'https://optiko-hamburg.de/',
      map: 'https://maps.app.goo.gl/kC7zDSW56SNyjPU98',
      lat: 53.5483526150994, 
      lon: 9.989690770948686,
    },
    {
      logo: '/assets/partner/logos/kaiyo.jpg',
      name: 'Kaiyo',
      description: '',
      website: 'https://kaiyo.eatbu.com/',
      map: 'https://maps.app.goo.gl/eBrFJpPaDoHjugwP6',
      lat: 53.548777942090425, 
      lon: 9.993980255604443,
    },
    {
      logo: '/assets/partner/logos/cafe_parthenope.png',
      name: 'Cafè & Bar Parthenope',
      description: '',
      website: 'https://www.instagram.com/parthenopeofficial/',
      map: 'https://maps.app.goo.gl/239JZrfJVRHkuLrH9',
      lat: 53.54924446193022, 
      lon: 9.993953697932273,
    },
    {
      logo: '/assets/partner/logos/VIVIRY-Abendkleider.jpg',
      name: 'VIVIRY',
      description: '',
      website: 'https://viviry.de/',
      map: 'https://maps.app.goo.gl/bAcoo4iTQuVyp9AU7',
      lat: 53.54953010172498, 
      lon: 9.992373440260156,
    },
    {
      logo: '/assets/partner/logos/oc_hairsystems_v_black.svg',
      name: 'O.C. Hairsystems',
      description: '',
      website: 'https://oc-hairsystems.com/',
      map: 'https://maps.app.goo.gl/wH33v91Jd5jaoqVs7',
      lat: 53.54536691962317, 
      lon: 9.988222486292901,
    },
    // {
    //   logo: '/assets/partner/logos/kiosk.jpg',
    //   name: 'Kiosk No.1',
    //   description: '',
    //   website: 'https://maps.app.goo.gl/gCNGVAMBuqtSd4ci8',
    // },
    {
      logo: '/assets/partner/logos/germanfinance.svg',
      name: 'GF German Finance',
      description: '',
      website: 'https://www.germanfinance.com/',
      map: 'https://maps.app.goo.gl/9nN7qfNHTo87UW6g6',
      lat: 53.552740193798115, 
      lon: 9.992727270948972,
    },
    {
      logo: '/assets/partner/logos/60_seconds.png',
      name: '60 seconds to napoli',
      description: '',
      website: 'https://60secondstonapoli.de/',
      map: 'https://maps.app.goo.gl/b5RNgcXpP84RR15x5',
      lat: 53.54875374419876, 
      lon: 9.991176355847506,
    },
    {
      logo: '/assets/partner/logos/vinfast.webp',
      name: 'VinFast',
      description: '',
      website: 'https://vinfastauto.eu/',
      map: 'https://maps.app.goo.gl/51inW7kUfxDjD14V6',
      lat: 53.54858158690102, 
      lon: 9.990462353751896,
    },
    {
      logo: '/assets/partner/logos/V-Zug_logo.png',
      name: 'V-ZUG Studio Hamburg',
      description: '',
      website: 'https://www.vzug.com/de/en/consultation/v-zug-studio-hamburg',
      map: 'https://maps.app.goo.gl/ihQJbq9moAue9sdT6',
      lat: 53.549089208601075, 
      lon: 9.991445082587973,
    },
    {
      logo: '/assets/partner/logos/ITPC.png',
      name: 'ITPC Hamburg',
      description: '',
      website: 'https://itpchamburg.de/',
      map: 'https://maps.app.goo.gl/J2s1HrUpBpEBFsrW6',
      lat: 53.55275310113352, 
      lon: 9.992688840260325,
    },
    {
      logo: '/assets/partner/logos/Holzwerk-Hamburg_Logo_Final_Black.webp',
      name: 'Holzwerk Hamburg',
      description: '',
      website: 'https://www.holzwerk-hamburg.de/',
      map: '53.54265052712116, 9.997215642112373',
      lat: 53.54269515443774, 
      lon: 9.997151269095907,
    },
    {
      logo: '/assets/partner/logos/4walls.png',
      name: '4 WALLS',
      description: '',
      website: 'https://www.4walls-hamburg.de/',
      map: 'https://maps.app.goo.gl/cvrNVtwDxcTT8vkg8',
      lat: 53.54545729556783, 
      lon: 9.986985323063125,
    },
    {
      logo: '/assets/partner/logos/Bao Bao - VegaN Livestyle Logo trans.png',
      name: 'Bao Bao',
      description: '',
      website: 'https://www.baobao-hamburg.de/',
      map: 'https://maps.app.goo.gl/gEGWmdePhaWBvfJW9',
      lat: 53.5907837023118, 
      lon: 9.988609997934315,
    },
    {
      logo: '/assets/partner/logos/Ohrwerk-RGB-Web.svg',
      name: 'OHRWERK Hörgeräte',
      description: '',
      website: 'https://www.ohrwerk-hoergeraete.de/',
      map: 'https://maps.app.goo.gl/ZR5CFUjsigNwnvFv6',
      lat: 53.55408090520927, 
      lon: 9.94375506837507,
    },
    {
      logo: '/assets/partner/logos/afroschick.jpg',
      name: 'AfroSchick',
      description: 'Vereint Mode, Kunst, Musik und Drinks zu einem einzigartigen Kultur-Erlebnis.\nAm Wochenende lädt der Brunch zum Genießen ein.\nDer Space feiert afrikanische Kultur und heißt alle mit positiven Vibes willkommen.',
      website: 'https://www.instagram.com/afroschick/?hl=de',
      map: 'https://maps.app.goo.gl/Ta1T23HQkeFd1UV18',
      lat: 53.57071148131852, 
      lon: 10.02713829793332,
    },
    {
      logo: '/assets/partner/logos/house-of-beauty-splash-logo.png',
      name: 'House of Beauty',
      description: '',
      website: 'https://houseofbeautybyanastasija.com/',
      map: 'https://maps.app.goo.gl/5r5K97CizcdgfvLE6',
      lat: 53.565288229270834, 
      lon: 10.023917069339271,
    },
    {
      logo: '/assets/partner/logos/dogs_gourmet.jpg',
      name: 'DOG\'s GOURMET',
      description: '',
      website: 'https://www.instagram.com/dogsgourmet_hamburg/',
      map: 'https://maps.app.goo.gl/BQZi5qQvTfbSxPEA8',
      lat: 53.54302235340303, 
      lon: 9.997670572801042,
    },
    {
      logo: '/assets/partner/logos/Wattloeper.jpeg',
      name: 'Wattlöper',
      description: '',
      website: 'https://www.wattloeper.com/',
      map: 'https://maps.app.goo.gl/hPwQaym1q3asu66C9',
      lat: 53.54307449926365, 
      lon: 9.997816226768089,
    },
    {
      logo: '/assets/partner/logos/ferdinand_holzmann_verlag_gmbh_logo.jpg',
      name: 'Ferdinand Holzmann Verlag',
      description: '',
      website: 'https://holzmann.de/',
      map: 'https://maps.app.goo.gl/TiqfikQMXVvbBoGg6',
      lat: 53.580818055434456, 
      lon: 10.027669155082059,
    },
    {
      logo: '/assets/partner/logos/I-LOVE-HH-01.png',
      name: 'I love HH',
      description: '',
      website: 'https://www.ilovehh.de/',
      map: 'https://maps.app.goo.gl/aNGkPi42DgTBrJef7',
      lat: 53.59301537963817, 
      lon: 9.832771281366707,
    },
    {
      logo: '/assets/partner/logos/gustavgruen.png',
      name: 'Gustav Grün',
      description: '',
      website: 'https://gustavgrün.de/',
      map: 'https://maps.app.goo.gl/63MJz9m3odtZfQDJ7',
      lat: 53.62603410332912, 
      lon: 9.906150313076328,
    },
    {
      logo: '/assets/partner/logos/Akkodis.png',
      name: 'Akkodis',
      description: '',
      website: 'https://www.akkodis.com/',
      map: 'https://maps.app.goo.gl/MvhBARxxELm6Lx4GA',
      lat: 53.54595705888023, 
      lon: 9.994767354602633,
    },
    {
      logo: '/assets/partner/logos/Allegresse.png',
      name: 'Allégresse',
      description: '',
      website: 'https://www.instagram.com/allegresse.hamburg/',
      map: 'https://maps.app.goo.gl/YYJmn2s82qudeeABA',
      lat: 53.59187697444319, 
      lon: 9.988513686295168,
    },
    {
      logo: '/assets/partner/logos/hth-logo.svg',
      name: 'HTH',
      description: '',
      website: 'https://www.hth.de/',
      map: 'https://maps.app.goo.gl/GZ4mDw6cua1HdZmM7',
      lat: 53.550408906853654, 
      lon: 9.992633338834034,
    },
    {
      logo: '/assets/partner/logos/Cosphatec_positiv_RBG.svg',
      name: 'Cosphatec',
      description: '',
      website: 'https://cosphatec.com/',
      map: 'https://maps.app.goo.gl/K9UkyoBtXsQn4LyU7',
      lat: 53.547818361967835, 
      lon: 9.989883342112611,
    },
    {
      logo: '/assets/partner/logos/ls_praxis.png',
      name: 'LS praxis',
      description: '',
      website: 'https://lspraxis.de/',
      map: 'https://maps.app.goo.gl/gNgmhdxgQa2nCmiG7',
      lat: 53.5901701181725, 
      lon: 9.98671411349176,
    },
    {
      logo: '/assets/partner/logos/Jöhnke_Reichow.jpg',
      name: 'Jöhnke & Reichow Rechtsanwälte',
      description: '',
      website: 'https://joehnke-reichow.de/',
      map: 'https://maps.app.goo.gl/4zLrY1aQoJC6Z72WA',
      lat: 53.5486382100953, 
      lon: 9.988704015129118,
    },
    {
      logo: '/assets/partner/logos/emma_sleep.svg',
      name: 'Emma',
      description: '',
      website: 'https://www.emma-sleep.com/',
      // map: '',
      // lat:
      // lon: ,
    },
    {
      logo: '/assets/partner/logos/property_team.png',
      name: 'Property Team AG',
      description: '',
      website: 'https://www.property-team.de/',
      map: 'https://maps.app.goo.gl/MUhXu3XeSRZc2rTGA',
      lat: 53.54841844909725, 
      lon: 9.988191311232425,
    },
    {
      logo: '/assets/partner/logos/IMPULS-LogoWortmarke@2x.jpg',
      name: 'IMPULS Personal',
      description: '',
      website: 'https://www.impuls-personal.com/',
      map: 'https://maps.app.goo.gl/w4EK6U5NPj6euDW5A',
      lat: 53.54849247017404, 
      lon: 9.987751540260122,
    },
    {
      logo: '/assets/partner/logos/Mentorics+energy+RGB.webp',
      name: 'Mentorics',
      description: '',
      website: 'https://www.mentorics.de/',
      map: 'https://maps.app.goo.gl/bycGLfgqNpGSbfHH6',
      lat: 53.548816688540526, 
      lon: 9.98796161142403,
    },
    {
      logo: '/assets/partner/logos/BF_Blumen.png',
      name: 'BF Blumen',
      description: '',
      website: 'https://bfblumen.de/',
      map: 'https://maps.app.goo.gl/HPzyS88NVgbJNaLQ8',
      lat: 53.54906179798069, 
      lon: 9.99088905745702,
    },
    {
      logo: '/assets/partner/logos/Jil_Sander_Logo.jpg',
      name: 'Jil Sander',
      description: '',
      website: 'https://www.jilsander.com/',
      map: 'https://maps.app.goo.gl/djiTfDPqbZMY9GqJ7',
      lat: 53.551397349031205, 
      lon: 9.990266311424222,
    },
    {
      logo: '/assets/partner/logos/Expert_Treuhand_Logo_times.jpg',
      name: 'EXPERT Treuhand GmbH',
      description: '',
      website: 'https://www.expertwpg.de/',
      map: 'https://maps.app.goo.gl/ezp3Acx2BfCYJRzCA',
      lat: 53.55154570440674, 
      lon: 9.989643151899465,
    },
    // {
    //   logo: '/assets/partner/logos/Zahnaerzte_am_neuen_Wall.jpg',
    //   name: 'Zahnärzte am neuen Wall',
    //   description: '',
    //   website: 'http://zahnaerzte-am-neuen-wall.de/',
    // },
    {
      logo: '/assets/partner/logos/hapeko-180x180-01_1-3.webp',
      name: 'HAPEKO',
      description: '',
      website: 'https://www.hapeko.de/',
      map: 'https://maps.app.goo.gl/S5WLdGpnJ3MRQD2w6',
      lat: 53.548839739657424, 
      lon: 9.99129977465387,
    },
    {
      logo: '/assets/partner/logos/treatwell.png',
      name: 'Paul Baker Hair and More',
      description: '',
      website: 'https://www.treatwell.de/',
      map: 'https://maps.app.goo.gl/NKSxSMEYRDJhTXd66',
      lat: 53.56792209109555, 
      lon: 9.98008145560536,
    },
    {
      logo: '/assets/partner/logos/Rabot-Energy-RGB-150dpi-Logo-schwarz-gruen-positiv-rand-zweizeilig.webp',
      name: 'Rabot Energy',
      description: '',
      website: 'https://www.rabot.energy/',
      map: 'https://maps.app.goo.gl/4KAvLXkUhAyUAqUU9',
      lat: 53.54814490984916, 
      lon: 9.990016055604396,
    },
    {
      logo: '/assets/partner/logos/pittarello_ohne.png',
      name: 'Ristorante Pittarello',
      description: '',
      website: 'https://www.ristorante-pittarello.de/',
      map: 'https://maps.app.goo.gl/P1ZmsUWgv16ooxvv6',
      lat: 53.58053822228242, 
      lon: 10.012717870950262,
    },
    {
      logo: '/assets/partner/logos/brunos-kaeseladen.jpg',
      name: 'Bruno´s Käseladen',
      description: '',
      website: 'https://brunos-kaeseladen.de/',
      map: 'https://maps.app.goo.gl/kRhmkvz6xEpkHnC16',
      lat: 53.561569287231436, 
      lon: 9.96254070957213,
    },
    {
      logo: '/assets/partner/logos/martini_chartering.png',
      name: 'Martini Chartering',
      description: '',
      website: 'https://www.martini-chartering.com/',
      map: 'https://maps.app.goo.gl/obhXk9fQZVyw9V1i6',
      lat: 53.55313497997949, 
      lon: 9.988884676537324,
    },
    {
      logo: '/assets/partner/logos/BV.png',
      name: 'Zahnärztinen am Rathausmarkt',
      description: '',
      website: 'https://www.rathaus.dental/',
      map: 'https://maps.app.goo.gl/EePUsVxfcfNAcPvp9',
      lat: 53.54990621481188, 
      lon: 9.993381943965272,
    },
    {
      logo: '/assets/partner/logos/spaccaforno.jpeg',
      name: 'Spaccaforno',
      description: '',
      website: 'https://spaccaforno.de/',
      map: 'https://maps.app.goo.gl/b9S9v2N2eqBDgDgX9',
      lat: 53.550927954031884, 
      lon: 9.990155696977297,
    },
    {
      logo: '/assets/partner/logos/MVZ55.jpg',
      name: 'MVZ Alter Wall 55',
      description: '',
      website: 'https://www.mvz55.de/',
      map: 'https://maps.app.goo.gl/ELtj3KFhf6yNMuZb6',
      lat: 53.54967448377363, 
      lon: 9.98907526909625,
    },
    {
      logo: '/assets/partner/logos/bloom-hamburg.png',
      name: 'Bloom Bekleidungsgeschäft',
      description: '',
      website: 'https://bloom-hamburg.de/',
      map: 'https://maps.app.goo.gl/qRUPnnp1Kj3zKY9X7',
      lat: 53.57257116924331, 
      lon: 9.959232477273412,
    },
    {
      logo: '/assets/partner/logos/MantiManti_rot_100x.png',
      name: 'Manti Manti',
      description: '',
      website: 'https://mantimanti.de/',
      map: 'https://maps.app.goo.gl/beNFac6wh38QSCPE8',
      lat: 53.58382325197769, 
      lon: 9.976573682803139,
    },
    {
      logo: '/assets/partner/logos/hansekind.jpg',
      name: 'Hansekind',
      description: '',
      website: 'https://www.hansekind.de/',
      map: 'https://maps.app.goo.gl/mdZfYiGcc4jga8Tf8',
      lat: 53.55327911476794, 
      lon: 9.988530597932483,
    },
    {
      logo: '/assets/partner/logos/Bizim-Berber.jpg',
      name: 'Bizim Berber',
      description: '',
      website: 'https://www.instagram.com/bizimberber.hh/',
      map: 'https://maps.app.goo.gl/FrxTVUYT17aDp5yN7',
      lat: 53.557880960867294, 
      lon: 10.011520196308865,
    },
    {
      logo: '/assets/partner/logos/New-York-Bagel-Bar.png',
      name: 'New York Bagel Bar',
      description: '',
      website: 'https://newyorkbagelbar.com/',
      map: 'https://maps.app.goo.gl/qYsS4x9oZdzNLA837',
      lat: 53.548131230672034, 
      lon: 9.98829692604853,
    },
    {
      logo: '/assets/partner/logos/zasa.png',
      name: 'ZASA',
      description: '',
      website: 'https://www.zasa.de/',
      map: 'https://maps.app.goo.gl/An8HdTpbtQZCvg297',
      lat: 53.559805555293956, 
      lon: 9.964619355605011,
    },
    {
      logo: '/assets/partner/logos/linette.png',
      name: 'Linette',
      description: '',
      website: 'https://www.linette.de/',
      map: 'https://maps.app.goo.gl/PAN5CakYUS3VXf3C8',
      lat: 53.584315754231206, 
      lon: 9.984956545620562,
    },
    {
      logo: '/assets/partner/logos/3dtrickart.png',
      name: 'MINDWAYS 3D TrickArt',
      description: '',
      website: 'https://3dtrickart.de/',
      map: 'https://maps.app.goo.gl/127qq8qQrihf9j456',
      lat: 53.54870470765702, 
      lon: 9.989240599784793,
    },
    {
      logo: '/assets/partner/logos/FDB.webp',
      name: 'FDB Møbler',
      description: '',
      website: 'https://www.fdbmobler.dk/',
      map: 'https://maps.app.goo.gl/YPQWRwrArYNouyRT6',
      lat: 53.55531384489816, 
      lon: 9.990226786293416,
    },
    {
      logo: '/assets/partner/logos/hamburg-de.png',
      name: 'hamburg.de',
      description: '',
      website: 'https://www.hamburg.de/',
      map: 'https://maps.app.goo.gl/qbHkRyW62snerSHo8',
      lat: 53.57249250994567, 
      lon: 9.990220169097372,
    },
    {
      logo: '/assets/partner/logos/asisam.png',
      name: 'ASISAM',
      description: 'ASISAM bietet hochwertige Shiatsu-Massagesessel mit persönlicher Beratung und eigenem Service.\nDas Unternehmen verwandelt Wohnräume in Wellness- Oasen und Büros in Erholungszonen.\nZiel ist mehr Entspannung und bessere Work - Life - Balance.',
      website: 'https://www.asisam.de/',
      map: 'https://maps.app.goo.gl/r4Rz4CbpZSaEPdxk7',
      lat: 53.54299263507041, 
      lon: 10.0034338016371,
    },
    {
      logo: '/assets/partner/logos/brillenhausblankenese.png',
      name: 'Brillenhaus Blankenese',
      description: 'Brillenhaus Blankenese ist seit 1984 Ihr Optiker mit Leidenschaft für Brillen und Kunst.\nNeben Designmarken und Retro- Modellen bietet das Geschäft auch wechselnde Kunstausstellungen.\nZum Service gehören Brillenglasbestimmung, Anpassung, Reinigung und Reparaturen.',
      website: 'https://brillenhaus-blankenese.de/',
      map: 'https://maps.app.goo.gl/8hSfqTwX9iihtqHo8',
      lat: 53.55933528328846, 
      lon: 9.811112624916294,
    },
    {
      logo: '/assets/partner/logos/hair-inn.webp',
      name: 'Hair Inn',
      description: 'Hair Inn – Ihr Friseur für stilvolle Schnitte, glänzende Farben und individuelle Beratung.\nOb klassisch, modern oder kreativ – hier stehen Qualität, Wohlfühlatmosphäre und Ihre Persönlichkeit im Mittelpunkt.',
      website: 'https://hairinnbymert.de/',
      map: 'https://maps.app.goo.gl/3Phuaft5aUjsBhA77',
      lat: 53.55968856571153, 
      lon: 9.933289636374603,
    },
    {
      logo: '/assets/partner/logos/homeandkids.png',
      name: 'home and kids',
      description: 'Home & Kids ist ein Concept Store in Hamburg-Blankenese mit Wohnaccessoires, Kindermöbeln und ausgesuchter Mode.\nDas Sortiment reicht von handgefertigten Möbeln bis zu liebevollen Spielsachen.\nEltern und Kinder finden hier Inspiration und eine entspannte Atmosphäre.',
      website: 'https://www.homeandkids.de/',
      map: 'https://maps.app.goo.gl/1QrXHLUvTpia5m8G8',
      lat: 53.564408753266974, 
      lon: 9.812611182588698,
    },
    {
      logo: '/assets/partner/logos/leos.png',
      name: 'Leo´s Café',
      description: 'Leo’s Café bietet Kaffeespezialitäten, Tee, Kuchen und kleine Speisen in gemütlicher Atmosphäre im Herzen Hamburgs.\nDie exklusive Davidoff Lounge lädt zum Genuss erlesener Zigarren in stilvollem Ambiente ein.',
      website: 'https://leos-cafe.com/',
      map: 'https://maps.app.goo.gl/xcxphdqieKCL25N3A',
      lat: 53.559268977971165, 
      lon: 9.991219233654897,
    },
    {
      logo: '/assets/partner/logos/nilsborstelmann.png',
      name: 'Nils Borstelmann',
      description: 'Seit über 40 Jahren steht das Unternehmen für hochwertige Bodenbeläge und individuelle Lösungen.\nEigene Handwerker verlegen und verarbeiten Sisal, textile und elastische Beläge fachgerecht.\nIm Fokus stehen persönliche Beratung und passgenaue Umsetzung.',
      website: 'https://nils-borstelmann.de/',
      map: 'https://maps.app.goo.gl/xQBi5CcXbFy9vDCq8',
      lat: 53.588932356536745, 
      lon: 9.986737374007772,
    },
  ];

  // Duplicate list once to enable seamless looping in the marquee.
  const marqueeItems = [...partnerLogos, ...partnerLogos];

  return (
    <Box>
      <Navbar />

      <Box
        sx={{
          top: { xs: '-114px', sm: '-114px', md: '-103px' },
          position: 'relative',
          backgroundImage: "url('/assets/partner/Partner_Header.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          width: '100vw',
          aspectRatio: 1920 / 538,
          marginLeft: 'calc(50% - 50vw)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-end',
          marginBottom: '-100px'
        }}
      >
      </Box>

      <Typography
        variant="h2"
        sx={{
          textAlign: 'center',
          marginBottom: '40px',
          marginTop: '40px',
          fontSize: { xs: '2rem', md: '3rem' },
        }}
      >
        Unsere Partner
      </Typography>

      {/* Partner Logos Section */}
      <Box
        sx={{
          backgroundColor: 'white',
        }}
      >
        <Box
          sx={{
            margin: '60px auto',
            marginBottom: isNonMobileScreens ? '160px' : '80px',
            position: 'relative',
            paddingTop: '40px',
            paddingBottom: '20px',
          }}
        >

          {/* Replace the entire banner JSX (where the Swiper used to be) with this single unit (no duplicate copy) */}
          <Box
            ref={scrollRef}
            onScroll={handleManualScroll}
            onPointerEnter={() => { isHoveringBannerRef.current = true; }}
            onPointerLeave={() => { isHoveringBannerRef.current = false; }}
            sx={{
              overflowX: 'auto',
              overflowY: 'hidden',
              whiteSpace: 'nowrap',
              pb: '20px',
              // Hide horizontal scrollbar while keeping scroll behavior.
              msOverflowStyle: 'none', // IE/Edge
              scrollbarWidth: 'none', // Firefox

              '&::-webkit-scrollbar': {
                display: 'none', // Chrome/Safari/Opera
              },
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                gap: 4,
                alignItems: 'stretch',
                px: 2,
              }}
            >
              {marqueeItems.map((partner, idx) => (
                <Box
                  key={`unit-${idx}`}
                  sx={{
                    position: 'relative',
                    width: isNonMobileScreens ? 260 : 180,
                    height: isNonMobileScreens ? 260 : 180,
                    flex: '0 0 auto',
                    overflow: 'hidden',
                    cursor: 'pointer',
                    backgroundColor: '#fff',
                  }}
                >
                  <Box
                    component="img"
                    src={partner.logo}
                    alt={partner.name}
                    sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                  />

                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      opacity: 0,
                      transition: 'opacity 0.3s ease',
                      textAlign: 'center',
                      '&:hover': { opacity: 1 },
                    }}
                  >
                    <Textfit
                      key={`${partner.name}-title-${forceRerender}`}
                      mode="multi"
                      min={10}
                      max={20}
                      forceSingleModeWidth={false}
                      style={{
                        width: '100%',
                        color: 'white',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        lineHeight: 1.2,
                        fontFamily: 'Monsterat, sans-serif',
                        marginBottom: '10px',
                        marginTop: '1rem',
                        boxSizing: 'border-box'
                      }}
                    >
                      {partner.name}
                    </Textfit>

                    <Box
                      sx={{
                        flexGrow: 1,
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        minHeight: 0,
                        maxHeight: '80%',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          minHeight: '80px',
                          maxHeight: '1000px',
                          mx: '1rem',
                        }}
                      >
                        <Textfit
                          key={`${partner.name}-${forceRerender}`}
                          mode="multi"
                          min={6}
                          max={19}
                          forceSingleModeWidth={false}
                          style={{
                            width: '100%',
                            height: '100%',
                            color: 'white',
                            textAlign: 'left',
                            whiteSpace: 'pre-line',
                            lineHeight: 1.2,
                            fontFamily: 'Monsterat, sans-serif',
                            overflow: 'hidden',
                            display: isNonMobileScreens ? 'flex' : 'none',
                            alignItems: 'center',
                          }}
                        >
                          {partner.description}
                        </Textfit>
                      </Box>
                    </Box>

                    <Typography
                      component="a"
                      href={partner.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      sx={{
                        color: 'white',
                        textDecoration: 'underline',
                        fontSize: { xs: '0.9rem', md: '1rem' },
                        fontWeight: 'bold',
                        '&:hover': { color: '#60ade1' },
                        mb: '1rem',
                        mt: '10px',
                      }}
                    >
                      Webseite besuchen
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

        </Box>
      </Box>

      <Box
        sx={{
          maxWidth: '1200px',
          margin: '0 auto',
          marginBottom: '20px',
          padding: '20px'
        }}
      >
        <Typography
          variant="h2"
          sx={{
            textAlign: 'center',
            marginBottom: '40px',
            marginTop: '40px',
            fontSize: { xs: '2rem', md: '2rem' },
          }}
        >
          Jetzt dabei sein!
        </Typography>
        <Typography >
          Werde Partner und fördere die Fachkräfte von morgen! Bringe Schüler, Wirtschaft und Kunst aktiv zusammen, gestalte eigene Events nach deinen Vorstellungen und schaffe damit wertvolle Räume für Austausch, Inspiration und kreative Impulse. Unterstütze junge Talente, ermögliche praxisnahe Erfahrungen und fördere die Verbindung zwischen Bildung und Unternehmertum. So trägst du zu einer starken, innovativen und kreativen Zukunft bei – voller Potenzial, Chancen und gemeinsamer Erfolge.
        </Typography>
      </Box>

      {/* Button as Image with Text Overlay */}
      <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: '120px' }}>
        <Box
          component={RouterLink}
          to="/kontakt"
          sx={{
            cursor: 'pointer',
            maxWidth: '228.5px',
            position: 'relative',
            display: 'inline-block',
            transition: 'transform 0.3s ease',
            '&:hover': {
              transform: 'scale(1.1)',
            },
          }}
        >
          <Box
            component="img"
            src="/assets/misc/Button.png"
            alt="Jetzt eintragen"
            sx={{
              width: '100%',
              height: 'auto',
              display: 'block',
              pointerEvents: 'none',
            }}
          />
          <Typography
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: 'white',
              fontFamily: 'Montserrat',
              fontStyle: 'italic',
              fontWeight: 'bold',
              textAlign: 'center',
              pointerEvents: 'none',
              fontSize: '0.9rem',
              whiteSpace: 'nowrap',
              width: '90%',
            }}
          >
            JETZT PARTNER WERDEN
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          padding: '20px',
          maxWidth: '1200px',
          margin: '0 auto',
          backgroundColor: theme.palette.background.paper,
          marginBottom: '60px',
        }}
      >
        {/* Map Section */}
        <Box>
          <Typography variant="h5" align="center" sx={{ fontWeight: 'bold', marginBottom: isNonMobileScreens ? '20px' : '40px' }}>
            Hier entsteht unser Partner-Guide
          </Typography>
          <Box sx={{ height: '480px', width: '100%', borderRadius: '8px', overflow: 'hidden' }}>
            <MapContainer
              center={[kunstkaleidoskopLocation.lat, kunstkaleidoskopLocation.lon]}
              zoom={16}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />

              {/* Kunstkaleidoskop marker */}
              <Marker
                position={[kunstkaleidoskopLocation.lat, kunstkaleidoskopLocation.lon]}
                icon={kunstkaleidoskopIcon}
                zIndexOffset={1000}
              >
                <Popup>
                  <Box sx={{ textAlign: 'center', minWidth: '200px' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                      {kunstkaleidoskopLocation.name}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {/* Route Button Wrapper */}
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Box
                          onClick={() => window.open(kunstkaleidoskopLocation.map, '_blank')}
                          sx={{
                            cursor: 'pointer',
                            maxWidth: '150px',
                            position: 'relative',
                            display: 'inline-block',
                            transition: 'transform 0.3s ease',
                            '&:hover': {
                              transform: 'scale(1.1)',
                            },
                          }}
                        >
                          <Box
                            component="img"
                            src="/assets/misc/Button.png"
                            alt="Route"
                            sx={{
                              width: '100%',
                              height: 'auto',
                              display: 'block',
                              pointerEvents: 'none',
                            }}
                          />
                          <Typography
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              color: 'white',
                              fontFamily: 'Montserrat',
                              fontStyle: 'italic',
                              fontWeight: 'bold',
                              textAlign: 'center',
                              pointerEvents: 'none',
                              fontSize: '1rem',
                              whiteSpace: 'nowrap',
                              width: '90%',
                              margin: '0 !important',
                              lineHeight: '1.0 !important'
                            }}
                          >
                            ROUTE
                          </Typography>
                        </Box>
                      </Box>

                      {/* Website Button Wrapper */}
                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Box
                          onClick={() => window.open(kunstkaleidoskopLocation.website, '_blank')}
                          sx={{
                            cursor: 'pointer',
                            maxWidth: '150px',
                            position: 'relative',
                            display: 'inline-block',
                            transition: 'transform 0.3s ease',
                            '&:hover': {
                              transform: 'scale(1.1)',
                            },
                          }}
                        >
                          <Box
                            component="img"
                            src="/assets/misc/Button.png"
                            alt="Website"
                            sx={{
                              width: '100%',
                              height: 'auto',
                              display: 'block',
                              pointerEvents: 'none',
                            }}
                          />
                          <Typography
                            sx={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%)',
                              color: 'white',
                              fontFamily: 'Montserrat',
                              fontStyle: 'italic',
                              fontWeight: 'bold',
                              textAlign: 'center',
                              pointerEvents: 'none',
                              fontSize: '1rem',
                              whiteSpace: 'nowrap',
                              width: '90%',
                              margin: '0 !important',
                              lineHeight: '1.0 !important'
                            }}
                          >
                            WEBSEITE
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Popup>
              </Marker>

              {/* Partner markers */}
              {partnerLogos
                .filter(partner => partner.lat && partner.lon)
                .map((partner, index) => (
                  <Marker
                    key={index}
                    position={[partner.lat, partner.lon]}
                    icon={partnerIcon}
                  >
                    <Popup>
                      <Box sx={{ textAlign: 'center', minWidth: '200px' }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                          {partner.name}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {/* Route Button Wrapper */}
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Box
                              onClick={() => window.open(partner.map, '_blank')}
                              sx={{
                                cursor: 'pointer',
                                maxWidth: '150px',
                                position: 'relative',
                                display: 'inline-block',
                                transition: 'transform 0.3s ease',
                                '&:hover': {
                                  transform: 'scale(1.1)',
                                },
                              }}
                            >
                              <Box
                                component="img"
                                src="/assets/misc/Button.png"
                                alt="Route"
                                sx={{
                                  width: '100%',
                                  height: 'auto',
                                  display: 'block',
                                  pointerEvents: 'none',
                                }}
                              />
                              <Typography
                                sx={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  color: 'white',
                                  fontFamily: 'Montserrat',
                                  fontStyle: 'italic',
                                  fontWeight: 'bold',
                                  textAlign: 'center',
                                  pointerEvents: 'none',
                                  fontSize: '1rem',
                                  whiteSpace: 'nowrap',
                                  width: '90%',
                                  margin: '0 !important',
                                  lineHeight: '1.0 !important'
                                }}
                              >
                                ROUTE
                              </Typography>
                            </Box>
                          </Box>

                          {/* Website Button Wrapper */}
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Box
                              onClick={() => window.open(partner.website, '_blank')}
                              sx={{
                                cursor: 'pointer',
                                maxWidth: '150px',
                                position: 'relative',
                                display: 'inline-block',
                                transition: 'transform 0.3s ease',
                                '&:hover': {
                                  transform: 'scale(1.1)',
                                },
                              }}
                            >
                              <Box
                                component="img"
                                src="/assets/misc/Button.png"
                                alt="Website"
                                sx={{
                                  width: '100%',
                                  height: 'auto',
                                  display: 'block',
                                  pointerEvents: 'none',
                                }}
                              />
                              <Typography
                                sx={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)',
                                  color: 'white',
                                  fontFamily: 'Montserrat',
                                  fontStyle: 'italic',
                                  fontWeight: 'bold',
                                  textAlign: 'center',
                                  pointerEvents: 'none',
                                  fontSize: '1rem',
                                  whiteSpace: 'nowrap',
                                  width: '90%',
                                  margin: '0 !important',
                                  lineHeight: '1.0 !important'
                                }}
                              >
                                WEBSEITE
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </Popup>
                  </Marker>
                ))}
            </MapContainer>
          </Box>
        </Box>
      </Box>

      {/* Divider / SVG to transition to Section 2 */}
      <Box sx={{ my: 4, width: "100vw", overflowX: "hidden" }}>
        <DIVIDER_SVG />
      </Box>

      <Box
        sx={{
          maxWidth: '1200px',
          margin: '0 auto',
          marginBottom: '120px',
          padding: isNonMobileScreens ? '0px' : '20px'
        }}
      >
        <Typography
          variant="h2"
          sx={{
            textAlign: 'center',
            marginBottom: '40px',
            marginTop: '40px',
            fontSize: { xs: '2rem', md: '2rem' },
          }}
        >
          Für Künstler
        </Typography>
        <Typography variant="body1">
          Unsere Galerie bietet Künstlern aller Disziplinen – ob Malerei, Fotografie, Skulptur, Performance oder digitale Kunst – eine Bühne, um deine Werke sichtbar zu machen und mit einem vielfältigen Publikum in Austausch zu treten.
          <br></br><br></br>
          Als Partner profitierst du von einer kostenlosen Ausstellungsmöglichkeit: Wir übernehmen für dich die vertrieblichen Tätigkeiten – von der Präsentation deiner Werke über die Kommunikation mit Interessierten bis hin zur Verkaufsabwicklung. So kannst du dich ganz auf dein künstlerisches Schaffen konzentrieren.
          <br></br><br></br>
          Darüber hinaus hast du die Möglichkeit, Workshops oder Kurse anzubieten und Teil eines kreativen Netzwerks zu werden, das Kunst, Bildung und Gemeinschaft miteinander verbindet. Gemeinsam schaffen wir inspirierende Erlebnisse für Kinder, Jugendliche und Erwachsene und fördern den Zugang zu künstlerischem Schaffen.
          <br></br><br></br>
          Nutze diese Gelegenheit, deine Leidenschaft und dein Können weiterzugeben, neue Kontakte zu knüpfen und die kulturelle Vielfalt unserer Galerie mitzugestalten.
        </Typography>

        {/* Button as Image with Text Overlay */}
        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '40px' }}>
          <Box
            component={RouterLink}
            to="/kontakt"
            sx={{
              cursor: 'pointer',
              maxWidth: '228.5px',
              position: 'relative',
              display: 'inline-block',
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'scale(1.1)',
              },
            }}
          >
            <Box
              component="img"
              src="/assets/misc/Button.png"
              alt="Jetzt eintragen"
              sx={{
                width: '100%',
                height: 'auto',
                display: 'block',
                pointerEvents: 'none',
              }}
            />
            <Typography
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: 'white',
                fontFamily: 'Montserrat',
                fontStyle: 'italic',
                fontWeight: 'bold',
                textAlign: 'center',
                pointerEvents: 'none',
                fontSize: '1.2rem',
                whiteSpace: 'nowrap',
                width: '90%',
              }}
            >
              JETZT ANFRAGEN
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        id="schweitzer"
        sx={{
          maxWidth: '1200px',
          margin: '0 auto',
          scrollMarginTop: '160px',
        }}
      >
        <Box
          sx={{
            width: '100vw',
            marginLeft: 'calc(50% - 50vw)',
            marginBottom: isNonMobileScreens ? 8 : 4,
          }}
        >
          <Box
            component="img"
            src="/assets/partner/Pamela_Schweitzer.jpg"
            alt="Pamela Schweitzer Kunstwerke"
            sx={{
              width: '100%',
              objectFit: 'contain',
            }}
          />
        </Box>

        <Box sx={{ padding: isNonMobileScreens ? '0px' : '20px' }}>
          <Typography variant="h3" sx={{ mb: 1 }}>
            Pamela Schweitzer
          </Typography>

          <Typography variant="body1" sx={{ mb: 2 }}>
            Wir freuen uns, die talentierte Künstlerin Pamela Schweizer in unserer Galerie willkommen zu heißen. Ihre charakteristischen Clown-Zeichnungen sind voller Ausdruckskraft und Detailreichtum – und ab sofort stellt sie ihre Kunst bei uns aus.
            <br></br><br></br>
            Besonders spannend: Pamela verbindet ihre Kunst mit unseren Kaleidoskopen. In kostenlosen Workshops für Schulkinder zeigt sie, wie aus einem schlichten Rohling ein einzigartiges Kunst-Kaleidoskop entsteht. So erleben Kinder nicht nur die Freude am Zeichnen, sondern gestalten etwas ganz Eigenes, das in unserer Galerie sichtbar wird.
            <br></br><br></br>
            Auch Erwachsene können kreativ werden: In 90-minütigen Kursen (90 Euro) vermittelt Pamela, wie man mit einfachen Techniken aus Ideen faszinierende Bilder entwickelt – und diese ebenfalls auf Kaleidoskopen zum Leben erweckt.
            <br></br><br></br>
            Nutze diese Gelegenheit, ein Kaleidoskop als deine eigene Leinwand zu gestalten und gleichzeitig von einer außergewöhnlichen Künstlerin zu lernen. Für weitere Informationen und zur Anmeldung besuche bitte unsere Kontaktseite und schreibe in das Nachrichtenfeld das Stichwort: <span style={{ fontStyle: "Italic" }}>Malkurs</span>
          </Typography>
        </Box>

        {/* Button as Image with Text Overlay */}
        <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '40px', marginBottom: '40px' }}>
          <Box
            component={RouterLink}
            to="/kontakt"
            sx={{
              cursor: 'pointer',
              maxWidth: '228.5px',
              position: 'relative',
              display: 'inline-block',
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'scale(1.1)',
              },
            }}
          >
            <Box
              component="img"
              src="/assets/misc/Button.png"
              alt="Jetzt eintragen"
              sx={{
                width: '100%',
                height: 'auto',
                display: 'block',
                pointerEvents: 'none',
              }}
            />
            <Typography
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: 'white',
                fontFamily: 'Montserrat',
                fontStyle: 'italic',
                fontWeight: 'bold',
                textAlign: 'center',
                pointerEvents: 'none',
                fontSize: '0.95rem',
                whiteSpace: 'nowrap',
                width: '90%',
              }}
            >
              HIER MALKURS BUCHEN
            </Typography>
          </Box>
        </Box>

      </Box>
      <Footer />
    </Box>
  );
};

export default PartnerPage;